/**
 * Doc list cache backed by localStorage.
 *
 * A page reload used to mean a full `/api/documents` round-trip before the grid
 * could paint anything. Reading localStorage is synchronous, so the first paint
 * now already shows the last-known notes, and the network is only touched when
 * that snapshot is older than STALE_AFTER_MS (or the user hits Refresh).
 *
 * The feed is paginated, so the snapshot is "every page loaded so far" plus the
 * cursor for the next one — a reload picks up scrolling right where it stopped.
 * Only the unfiltered feed is cached; a filtered one is always fetched fresh.
 */
const STORAGE_KEY = 'recycle-brain:docs-cache:v1';

export const STALE_AFTER_MS = 5 * 60 * 1000;

const read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.docs)) return null;
    return {
      docs: parsed.docs,
      fetchedAt: Number(parsed.fetchedAt) || 0,
      nextCursor: parsed.nextCursor || null,
      counts: parsed.counts || null,
    };
  } catch {
    return null;
  }
};

export const readCachedDocs = () => read()?.docs || [];

export const readCachedCursor = () => read()?.nextCursor || null;

export const readCachedCounts = () => read()?.counts || null;

/** ms since the cache last came back from the backend; Infinity if never. */
export const getCacheAge = () => {
  const at = read()?.fetchedAt || 0;
  return at ? Date.now() - at : Infinity;
};

export const isCacheStale = () => getCacheAge() > STALE_AFTER_MS;

/**
 * `fetched: true` stamps the snapshot as server-fresh (restarts the TTL);
 * local edits pass false so they update the content without pretending we
 * just talked to the backend. `nextCursor` / `counts` left undefined keep
 * whatever the snapshot already had.
 */
export const writeCachedDocs = (docs, { fetched = true, nextCursor, counts } = {}) => {
  try {
    const prev = read();
    const fetchedAt = fetched ? Date.now() : prev?.fetchedAt || 0;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        docs: Array.isArray(docs) ? docs : [],
        fetchedAt,
        nextCursor: nextCursor === undefined ? prev?.nextCursor || null : nextCursor,
        counts: counts === undefined ? prev?.counts || null : counts,
      }),
    );
  } catch {
    // Quota / private mode — the cache is an optimisation, never a requirement.
  }
};

export const clearDocCache = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
};
